/* ----------------------------------------------------------------------------
 * Aura Voice — core types (provider-neutral, no DOM, no import.meta).
 *
 * This module is deliberately runtime-pure so it can be exercised by Node's
 * native test runner AND type-checked by the app build. It carries NO browser
 * or provider dependency: STT/TTS providers sit behind the adapter interfaces
 * (see ./stt, ./tts) and Aura's "brain" stays the EXISTING AI Gateway/Copilot
 * — voice is only a new I/O modality (research doc §0 REUSE MANDATE).
 * -------------------------------------------------------------------------- */

/** The five explicit voice states (research doc §2 beta client controls). */
export type VoiceStatus = 'idle' | 'listening' | 'thinking' | 'speaking' | 'error';

/** A structured, user-safe error (never leaks provider/model identity). */
export interface VoiceError {
  code:
    | 'mic_permission_denied'
    | 'mic_unavailable'
    | 'stt_failed'
    | 'tts_failed'
    | 'network'
    | 'reasoning_failed'
    | 'unknown';
  /** Human-facing, advisory message. */
  message: string;
}

/**
 * An action Aura proposes that would have an EXTERNAL effect (e.g. hand a
 * prompt to the campaign creator, send a reminder). It is NEVER executed by the
 * state machine — it is parked as `pendingApproval` until the user explicitly
 * APPROVEs (the confirm step). "AI proposes; a human decides."
 */
export interface ProposedAction {
  /** stable id for the action kind (e.g. 'prompt_to_campaign'). */
  kind: string;
  /** short, human-facing summary of what will happen on approval. */
  summary: string;
  /** opaque payload the caller needs to actually perform the action. */
  payload?: Record<string, unknown>;
}

/** Female-Aura persona framing (character contract §2) — display metadata only. */
export interface AuraPersona {
  displayName: string; // 'Aura'
  pronoun: 'she';
  accent: 'en-GB';
  /** recommended beta voice id behind the TTS adapter (config, not code). */
  voiceId: string; // 'aura-2-pandora-en'
}

export const DEFAULT_AURA_PERSONA: AuraPersona = {
  displayName: 'Aura',
  pronoun: 'she',
  accent: 'en-GB',
  voiceId: 'aura-2-pandora-en',
};
